/* eslint-disable camelcase */
import { useCallback } from 'react';

import type { AxiosResponse } from 'axios';

import type { ErrorType } from 'state/custom-instance';
import type {
  Address,
  Freelancer,
  KVKDetails,
} from 'state/__generated__/tadaahAPI';
import { useApiFreelancerPartialUpdate } from 'state/__generated__/tadaahAPI';
import client from 'state/remote/client';

import { useGetKVKDetails } from './use-get-kvk-details';
import { useGetFreelancerDetails } from './use-get-freelancer-details';

type UseUpdateFreelancerKVKInformationHandler = {
  addressOptions: () => { label: string; value: string }[];
  companyNameOptions: () => { label: string; value: string }[];
  initialValues: UpdateFreelancerKVKInformationType;
  error: ErrorType<unknown> | null;
  isLoading: boolean;
  isSuccess: boolean;
  updateFreelancerKvkInformation: (
    values: UpdateFreelancerKVKInformationType,
  ) => Promise<AxiosResponse<Freelancer, unknown>>;
  KVKData: KVKDetails | undefined;
  KVKError: ErrorType<{ [key: string]: string }> | null;
  KVKIsLoading: boolean;
  KVKIsSuccess: boolean;
  KVKRefetch: (options?: {
    throwOnError?: boolean;
    cancelRefetch?: boolean;
  }) => unknown;
};

export interface UpdateFreelancerKVKInformationType {
  kvk_nummer: string | null | undefined;
  company_name: string | null | undefined;
  address: Address | undefined;
  sbi_codes: string | null | undefined;
}

const useUpdateFreelancerKvkInformation = (
  params: { kvk_number: string },
  enabled: boolean,
): UseUpdateFreelancerKVKInformationHandler => {
  const { data } = useGetFreelancerDetails();

  const {
    data: KVKData,
    error: KVKError,
    isLoading: KVKIsLoading,
    isSuccess: KVKIsSuccess,
    refetch: KVKRefetch,
  } = useGetKVKDetails({ kvk_number: params.kvk_number }, enabled);

  const existingWorkAddress = data?.addresses?.find(
    (address) => address.address_type === 'Work',
  );

  const initialValues: UpdateFreelancerKVKInformationType = {
    address: existingWorkAddress,
    company_name: data?.company_name,
    kvk_nummer: data?.kvk_nummer,
    sbi_codes: data?.sbi_codes,
  };

  const { mutateAsync, isLoading, error, isSuccess } =
    useApiFreelancerPartialUpdate({
      mutation: {
        mutationKey: 'update-freelancer-kvk-information',
        onSuccess: () => {
          const queriesToInvalidate = [
            'freelancer-profile',
            'freelancer-details',
            'freelancer-signup-status',
          ];
          queriesToInvalidate.forEach((query) => {
            client.invalidateQueries(query);
          });
        },
      },
    });

  const updateFreelancerKvkInformation = useCallback(
    async (values: UpdateFreelancerKVKInformationType) => {
      if (existingWorkAddress && values.address)
        Object.assign(values.address, { id: existingWorkAddress.id });

      const response = await mutateAsync({
        data: {
          ...values,
          addresses: [values.address],
        } as unknown as Freelancer,
      });
      if (response.status !== 200) throw new Error('Something went wrong');

      return response;
    },
    [existingWorkAddress, mutateAsync],
  );

  const companyNameOptions = () => {
    if (KVKData?.company_names)
      return KVKData.company_names.map((company_name) => {
        return { label: company_name, value: company_name };
      });
    if (!KVKData && initialValues.company_name)
      return [
        {
          label: initialValues.company_name,
          value: initialValues.company_name,
        },
      ];
    return [];
  };

  const addressOptions = () => {
    if (KVKData?.addresses)
      return KVKData.addresses?.map((address) => {
        return {
          label: address.raw_address as string,
          value: address.raw_address as string,
        };
      });
    if (!KVKData && initialValues.company_name)
      return [
        {
          label: initialValues.address?.raw_address as string,
          value: initialValues.address?.raw_address as string,
        },
      ];
    return [];
  };

  return {
    addressOptions,
    companyNameOptions,
    error,
    initialValues,
    isLoading,
    isSuccess,
    KVKData,
    KVKError,
    KVKIsLoading,
    KVKIsSuccess,
    KVKRefetch,
    updateFreelancerKvkInformation,
  };
};

export default useUpdateFreelancerKvkInformation;
